import React, { useRef } from "react";
import PropTypes from "prop-types";
import { DropDownButton } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import "./DropDownButton.scss";

/**
 * @typedef {Object} items
 * @property {string} text - Text
 * @property {string} icon - Icon
 */

/**
 * Dropdown button
 * @module
 * @param {string} text - Button text
 * @param {items[]} items - Items to choose from
 * @param {string} [className] - ClassName of DropDownButton
 * @param {string} [buttonClassName] - ButtonClassName
 * @param {string} [icon] - Icon name
 * @param {string} [imageUrl] - Image URL
 * @param {func} onClick - Function invoked on button click
 * @param {bool} [disabled] - Disabled button
 * @param {object} [popupSettings] - Popup settings
 * @param {func} [itemRender] - itemRender function
 * @param {string} title - Button tooltip
 * @param {func} [customTooltipContent] - Custom tooltip content
 */
const DropDownButtonComponent = (props) => {
  const ref = useRef(null);
  const onClick = (e) => {
    if (e?.item?.disableClick) {
      return;
    }
    if (e.itemIndex !== undefined) {
      if (e?.item?.onItemClick) {
        e.item.onItemClick(e.itemIndex, e.item);
        return;
      }

      const div = document.body.querySelector(
        "div.k-animation-container.k-animation-container-fixed.k-animation-container-shown"
      );
      if (div) {
        div.style.display = "none";
      }

      props.onClick(e.itemIndex, e.item.text);
    }
  };
  const onOpen = (e) => {
    setTimeout(() => {
      const div = document.body.querySelector(".k-animation-container.k-animation-container-relative > .k-popup");
      if (div) {
        const rec = div.parentElement.getBoundingClientRect();
        if (rec.right > window.innerWidth) {
          div.parentElement.style.left = (rec.left - rec.right + window.innerWidth) + "px";
        }
      }
    }, 100);
  }

  const itemRender = (itemProps) => {
    if (props.itemRender) {
      return props.itemRender(itemProps);
    }

    const title = itemProps.item.title ? itemProps.item.title : itemProps.item.text;
    return (
      <div data-tooltip={title}>
        {itemProps.item.imageUrl ? (
          <img
            className="k-image"
            role="presentation"
            src={itemProps.item.imageUrl}
            alt=""
            data-tooltip={title}
          />
        ) : (
          <span
            className={
              itemProps.item.iconClass
                ? itemProps.item.iconClass
                : `k-icon k-i-${itemProps.item.icon}`
            }
            role="presentation"
            data-tooltip={title}
          />
        )}
        {itemProps.item.text}
      </div>
    );
  };

  return (
    <div className="dropdown-button">
      <Tooltip
        anchorElement="target"
        content={
          props.customTooltipContent ? () => props.customTooltipContent : null
        }
        position="top"
        openDelay={0}
        targetElement={ref.current}
      >
        <div
          ref={ref}
          title={props.title}
        >
          <DropDownButton
            text={props.text}
            items={props.items}
            className={props.className}
            buttonClass={`${props.className || ""} ${props.imageUrl || props.icon ? props.text ? "icon-text-button" : "icon-button" : ""}`}
            icon={props.icon}
            imageUrl={props.imageUrl ? props.imageUrl : null}
            fillMode={props.fillMode || props.look ? props.fillMode || props.look : props.icon || props.imageUrl ? "flat" : "solid"}
            onItemClick={(e) => onClick(e)}
            onOpen={(e) => onOpen(e)}
            disabled={props.disabled}
            popupSettings={props.popupSettings}
            itemRender={itemRender}
            themeColor={props.primary ? "primary" : null}
          />
        </div>
      </Tooltip>
    </div>
  );
};

DropDownButtonComponent.propTypes = {
  text: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      icon: PropTypes.string,
    })
  ).isRequired,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  icon: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  popupSettings: PropTypes.object,
  itemRender: PropTypes.func,
  title: PropTypes.string,
  customTooltipContent: PropTypes.object,
  imageUrl: PropTypes.string,
};

export default DropDownButtonComponent;
